import {
  AxisSwitcher,
  BorderGridContainer,
  Button,
  GridBox,
  H2,
  PortableText,
  Section,
  SpacerWrapper,
} from '@ahmdigital/ui';
import { chunk, isEmpty, map } from 'lodash/fp';

import { SellingPoint, SellingPointGroup } from '../../../../../types/__codegen__/sanity/graphql';
import InclusionsItem from './components/inclusion-item';
import styles from './styles.module.sass';
import uncapFpIterator from '../../../../../../utils/uncap-fp-iterator';

const mediumSizeThreshold = '46rem';
const inclusionsPerRow = 2;

type InclusionsProps = Pick<SellingPointGroup, 'sellingPointGroup'> & {
  // TODO: [AP-1892] Fix code/type drift between sanity and components with fields like description/summary/slogan(Raw) etc…
  // GraphQL fragments alias sloganRaw→slogan, summaryRaw→summary, urlObject→cta (see selling-point-fields fragment)
  // So components receive 'slogan', 'summary', 'cta', not the codegen 'sloganRaw'/'summaryRaw'/'urlObject' types
  slogan?: SellingPointGroup['sloganRaw'];
  summary?: SellingPointGroup['summaryRaw'];
  cta?: SellingPointGroup['urlObject'];
};

type SellingPointWithSummary = SellingPoint & {
  summary: SellingPoint['summaryRaw'];
};

const Inclusions = ({ cta, slogan, summary, sellingPointGroup }: InclusionsProps) => {
  if (isEmpty(sellingPointGroup)) {
    return null;
  }

  const inclusionsGroupedByRow = chunk(inclusionsPerRow, sellingPointGroup);

  return (
    <BorderGridContainer>
      <GridBox padding="0">
        <Section variant={['paddingTopSpacer1', 'paddingBottomSpacer1', styles.section]}>
          {slogan && (
            <H2 variant="marginBottomSpacer8">
              <PortableText source={slogan} />
            </H2>
          )}
          {summary && <PortableText source={summary} variant={styles.summary} />}
          <SpacerWrapper variant={['marginBottomSpacer4', 'marginTopSpacer3']}>
            {uncapFpIterator(map)(
              (row: SellingPointWithSummary[], index: number) => (
                <AxisSwitcher key={index} limit={inclusionsPerRow} space="0" threshold={mediumSizeThreshold}>
                  {map(
                    ({ summary: inclusionSummary, name: inclusionName }) => (
                      <InclusionsItem description={inclusionSummary} itemTitle={inclusionName} key={inclusionName} />
                    ),
                    row,
                  )}
                </AxisSwitcher>
              ),
              inclusionsGroupedByRow,
            )}
          </SpacerWrapper>
          {cta?.url && cta?.name && (
            <Button variant={['white', 'border']} to={cta.url}>
              {cta.name}
            </Button>
          )}
        </Section>
      </GridBox>
    </BorderGridContainer>
  );
};

export default Inclusions;
